import { useRef, useState } from "react";
import { ManagementApi } from "./api";
import { Locale } from "./copy";
import { ToolModal } from "./ToolModal";

export interface ArchivableSchedule {
  schedule_id: string;
  schedule_version: number;
  name: { en: string; es: string };
  ends_at: string;
  archived: boolean;
  archive_version: number;
  archive_available: boolean;
}

export function latestSchedules(rows: ArchivableSchedule[]): ArchivableSchedule[] {
  const latest = new Map<string, ArchivableSchedule>();
  for (const row of rows) {
    if ((latest.get(row.schedule_id)?.schedule_version ?? 0) < row.schedule_version) latest.set(row.schedule_id, row);
  }
  return [...latest.values()];
}

export class SessionArchiveAction {
  private pending?: { fingerprint: string; key: string };
  async submit(api: ManagementApi, hallId: string, row: ArchivableSchedule, name: string): Promise<void> {
    if (!row.archive_available) throw new Error("Archive is awaiting its database upgrade.");
    if (name !== row.name.en && name !== row.name.es) throw new Error("Type the exact session name.");
    const path = `/v1/halls/${encodeURIComponent(hallId)}/game-sessions/${encodeURIComponent(row.schedule_id)}/${row.archived ? "restore" : "archive"}`;
    const body = { hall_id: hallId, schedule_id: row.schedule_id, schedule_version: row.schedule_version,
      expected_archive_version: row.archive_version, confirm_name: name };
    const fingerprint = JSON.stringify([path, body]);
    if (this.pending?.fingerprint !== fingerprint) this.pending = { fingerprint, key: crypto.randomUUID() };
    await api.mutate(path, "POST", body, this.pending.key);
    this.pending = undefined;
  }
}

export function SessionArchiveManager({ api, hallId, locale, writable, onChange }: {
  api: ManagementApi; hallId: string; locale: Locale; writable: boolean; onChange: () => Promise<void>;
}): React.JSX.Element {
  const es = locale === "es";
  const [open, setOpen] = useState(false), [archived, setArchived] = useState(false);
  const [rows, setRows] = useState<ArchivableSchedule[]>([]), [selected, setSelected] = useState("");
  const [name, setName] = useState(""), [error, setError] = useState(""), [busy, setBusy] = useState(false);
  const lock = useRef(false), action = useRef(new SessionArchiveAction());
  const row = rows.find(item => item.schedule_id === selected);
  const load = async (showArchived: boolean): Promise<void> => {
    const result = await api.get<ArchivableSchedule[]>(`/v1/halls/${encodeURIComponent(hallId)}/game-sessions?archived=${showArchived}`);
    setRows(latestSchedules(result));
  };
  const show = async (showArchived: boolean): Promise<void> => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setOpen(true); setArchived(showArchived);
    setRows([]); setSelected(""); setName(""); setError("");
    try { await load(showArchived); }
    catch { setError(es ? "No se pudieron cargar las sesiones." : "Sessions could not be loaded."); }
    finally { lock.current = false; setBusy(false); }
  };
  return <>
    <button onClick={() => void show(false)}>{es ? "Archivar / restaurar sesiones" : "Archive / restore sessions"}</button>
    {open && <ToolModal title={es ? "Archivo recuperable de sesiones" : "Recoverable session archive"} close={() => { if (!lock.current) setOpen(false); }}>
      <form onSubmit={event => {
        event.preventDefault();
        if (!row || !writable || lock.current) return;
        lock.current = true; setBusy(true); setError("");
        void action.current.submit(api, hallId, row, name).then(async () => {
          setSelected(""); setName("");
          await Promise.all([load(archived), onChange()]);
        }).catch(failure => setError(failure instanceof Error ? failure.message : "Action not confirmed. Refresh and check the current list."))
          .finally(() => { lock.current = false; setBusy(false); });
      }}>
        <fieldset disabled={busy}>
          <div className="tool-modal-body">
            <p>{es ? "Archivar oculta sesiones pasadas de la lista normal. No borra cartones, ventas, llamadas, premios ni auditoría. Las sesiones publicadas deben estar cerradas y sus paquetes y reservas vencidos." : "Archiving hides past sessions from the normal list. It does not delete cards, sales, calls, awards or audit history. Published sessions must be closed, with all packages and leases expired."}</p>
            <p>{es ? "Restaurar solo vuelve a mostrar la sesión; no la reactiva ni libera inventario." : "Restoring only returns the session to the list; it does not restart play or free inventory."}</p>
            <label>{es ? "Mostrar" : "Show"}<select value={archived ? "archived" : "visible"} onChange={event => void show(event.target.value === "archived")}>
              <option value="visible">{es ? "Sesiones visibles" : "Visible sessions"}</option>
              <option value="archived">{es ? "Sesiones archivadas" : "Archived sessions"}</option>
            </select></label>
            <label>{es ? "Sesión" : "Session"}<select required value={selected} onChange={event => { setSelected(event.target.value); setName(""); setError(""); }}>
              <option value="">{es ? "Seleccione una sesión" : "Select a session"}</option>
              {rows.map(item => <option key={item.schedule_id} value={item.schedule_id}>{item.name[locale]} · v{item.schedule_version}</option>)}
            </select></label>
            {!rows.length && !busy && <p>{es ? "No hay sesiones en esta lista." : "No sessions in this list."}</p>}
            {row && <>
              <p>{row.schedule_id} · v{row.schedule_version}</p>
              {!row.archive_available && <p role="status">{es ? "Esperando actualización de la base de datos." : "Awaiting database upgrade."}</p>}
              {!row.archived && Date.parse(row.ends_at) >= Date.now() && <p role="status">{es ? "Esta sesión aún no ha terminado." : "This session has not ended yet."}</p>}
              {writable && <label>{es ? "Escriba el nombre exacto para confirmar" : "Type the exact session name to confirm"}<input required maxLength={120} value={name} onChange={event => setName(event.target.value)} autoComplete="off" /></label>}
            </>}
            {error && <p role="alert">{error}</p>}
          </div>
          <footer className="tool-modal-footer">
            <button type="button" onClick={() => setOpen(false)}>{es ? "Cerrar" : "Close"}</button>
            {writable && <button className="primary" type="submit" disabled={!row || !row.archive_available || (name !== row.name.en && name !== row.name.es) || (!row.archived && Date.parse(row.ends_at) >= Date.now())}>
              {archived ? (es ? "Restaurar sesión" : "Restore session") : (es ? "Archivar sesión" : "Archive session")}
            </button>}
          </footer>
        </fieldset>
      </form>
    </ToolModal>}
  </>;
}
